// ===============================
// Constants & Helpers
// ===============================
const FILTER_KEY_MAP = {
  'product_product_custom': 'filter.p.m.custom.product',
  'product_style_sub_product_custom': 'filter.p.m.custom.style_sub_product',
  'product_Polish': 'filter.v.option.polish',
  'product_Color': 'filter.v.m.custom.color',
  'product_metal_custom': 'filter.p.m.custom.metal',
};

const getPrevBtn = () => document.querySelector('.Previous-btn');

// Every question renders into the SAME .find-content-box-main - there is no
// navigation between steps, so the scroll offset simply carries over. On a long
// grid (Wedding Guest > Reception) that opened the next question already scrolled
// past its first row, so it read as starting from the middle. Instant, not smooth:
// a smooth window scroll on every render is exactly the "yank" the two notes in
// updateCards/updateShopifyCards warn about, and it fights the entrance animation.
// Anchored to .mainFinderView rather than a hardcoded 0 so it stays correct if
// anything is ever rendered above the wizard.
const jfScrollToStepTop = () => {
  const view = document.querySelector('.mainFinderView');
  const top = view ? window.pageYOffset + view.getBoundingClientRect().top : 0;
  window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'auto' });
};

const normalizeFilterKey = (rawKey) =>
  rawKey?.startsWith?.('filter.') ? rawKey : (FILTER_KEY_MAP[rawKey] || null);

const buildQueryFromFilters = (filtersObj) => {
  const params = new URLSearchParams();
  for (const rawKey in filtersObj) {
    const key = normalizeFilterKey(rawKey);
    if (!key) continue;
    [...new Set(filtersObj[rawKey])].forEach(v => params.append(key, v));
  }
  params.append('sort_by', 'manual');
  const currentParams = new URLSearchParams(window.location.search);
  const previewThemeId = currentParams.get('preview_theme_id');
  if (previewThemeId) {
    params.append('preview_theme_id', previewThemeId);
  }
  return params.toString();
};

// Tiny waiter (used by auto-start)
function waitFor(testFn, { interval = 150, timeout = 7000 } = {}) {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const id = setInterval(() => {
      try {
        if (testFn()) {
          clearInterval(id);
          resolve(true);
        } else if (Date.now() - start > timeout) {
          clearInterval(id);
          reject(new Error('waitFor: timeout'));
        }
      } catch (e) {
        clearInterval(id);
        reject(e);
      }
    }, interval);
  });
}

// ===============================
// Offline fallback (bundled snapshot)
// The middleware CORS allowlist only accepts kushals.com, so preview/draft
// theme origins (*.shopifypreview.com) get a 403 on every call. Load the
// snapshot asset lazily — only once a live call has actually failed.
// ===============================
let fallbackPromise = null;
function loadFallback() {
  if (window.JF_FALLBACK) return Promise.resolve(window.JF_FALLBACK);
  if (!window.jfFallbackUrl) return Promise.resolve(null);
  if (!fallbackPromise) {
    fallbackPromise = new Promise((resolve) => {
      const s = document.createElement('script');
      s.src = window.jfFallbackUrl;
      s.onload = () => resolve(window.JF_FALLBACK || null);
      s.onerror = () => resolve(null);
      document.head.appendChild(s);
    });
  }
  return fallbackPromise;
}

// ===============================
// CleverTap — Jewellery Finder funnel
//
// Every JF event lives here rather than in clevertap_custom.js.liquid, because
// they all need finder state (which question is on screen, which tiles were
// picked) that only this file holds. The CTA on the homepage / wedding page still
// records the entry page there, since that is the only place that knows it.
//
// The progress rail is Occasion -> Function -> Category, so the question number
// maps straight onto the three params the spec asks for.
// ===============================
const JF_ENTRY_PAGE_KEY = 'jf_entry_page';

// CleverTap: both finders fire the same generic event names (finder_started,
// finder_q{n}_clicked, ...). This `feature` param is what tells them apart.
const JF_FEATURE = 'Jewellery Finder';

// Written on the PLP hand-off, read once by assets/custom.js on the PLP to fire
// visited_plp_from_gift_finder. Shared with gift-for-her-find.js — the
// `feature` field inside says which finder wrote it.
const FINDER_VISITED_PLP_KEY = 'finder_visited_plp_params';

// The question the customer left from on the way to the PLP, kept outside the
// history stack so the trip back can rebuild it — see jfRestoreAfterPlp().
const JF_PLP_STATE_KEY = 'jf_plp_return_state';
// Ignore a stored view older than this, so a marker left behind by a finished
// run cannot hijack a reload much later in the same tab.
const JF_PLP_STATE_MAX_AGE = 30 * 60 * 1000;

const JF_PARAM_BY_QUESTION = {
  1: 'Finder_Occasion',
  2: 'Finder_Occasion_Type',
  3: 'Finder_Product_Types',
};

// { 1: ['Bridal'], 2: ['Haldi'], 3: ['Earring', 'Maang Tikka'] }
let jfAnswers = {};
// One "Started" per view of the finder. The start button is auto-clicked on load
// (sections/jewellery-find.liquid), and a tap on it can follow, which would
// otherwise report the funnel entry twice.
let jfStarted = false;

// Exit reporting state. jfGoToPlp() is the completion path, so the navigation it
// schedules must never be counted as an exit — jfHandingOffToPlp marks that
// window. jfExitReported keeps it to one Exit per run however the customer
// leaves: the top back button, a link out, a back gesture off the page, or the
// tab closing.
let jfHandingOffToPlp = false;
let jfExitReported = false;

// clevertap loads async and is absent on a draft/preview origin, so every push
// below is gated on this rather than assuming the global is there.
const ctReady = () => typeof clevertap !== 'undefined' && !!clevertap?.event?.push;

// Where the customer entered the finder from. clevertap_custom.js.liquid writes
// this on the CTA click; a direct landing has nothing stored, so fall back to
// naming the referrer.
function jfEntryPage() {
  try {
    const stored = sessionStorage.getItem(JF_ENTRY_PAGE_KEY);
    if (stored) return stored;
  } catch (e) {}

  const referrer = document.referrer || '';
  if (!referrer) return 'Direct';
  try {
    const url = new URL(referrer);
    if (url.origin !== window.location.origin) return 'External';
    if (url.pathname === '/') return 'Homepage';
    if (url.pathname.startsWith('/products/')) return 'PDP';
    if (url.pathname.startsWith('/collections/')) return 'PLP';
    return url.pathname;
  } catch (e) {
    return 'Direct';
  }
}

// The question currently on screen. updateCards()/updateShopifyCards() bump
// currentIndex after rendering, so it always runs one ahead of the question.
const jfCurrentQuestion = () => Math.max(currentIndex - 1, 1);

function jfRecordAnswer(questionNumber, tileNames) {
  const clean = (tileNames || [])
    .map((name) => String(name == null ? '' : name).trim())
    .filter(Boolean);
  if (!clean.length) return [];
  jfAnswers[questionNumber] = clean;
  return clean;
}

// The preference set built so far, as the spec's three params. Steps the customer
// has not reached yet are left out rather than sent empty.
function jfPreferenceParams() {
  const params = {};
  Object.keys(JF_PARAM_BY_QUESTION).forEach((question) => {
    const values = jfAnswers[question] || [];
    if (values.length) params[JF_PARAM_BY_QUESTION[question]] = values.join(', ');
  });
  return params;
}

// Fires Q{n}_Clicked for the question just answered and remembers the answer for
// the preference / PLP events at the end of the flow.
function jfTrackQuestionAnswered(tileNames) {
  const questionNumber = jfCurrentQuestion();
  const answered = jfRecordAnswer(questionNumber, tileNames);
  if (!answered.length) return;

  if (ctReady()) {
    const params = { feature: JF_FEATURE, "Tile Name": answered.join(", ") };
    // Q2 onwards also carries the answer of the question before it.
    if (questionNumber > 1) {
      params.Previous_Selected_Tile = (jfAnswers[questionNumber - 1] || []).join(", ");
    }
    clevertap.event.push(`finder_q${questionNumber}_clicked`, params);
  }
}

// End of the flow: report the full preference set, then the PLP hand-off, then
// navigate. Both carry the same selections, with the category answer doubling as
// the Product Type.
function jfGoToPlp(collectionHandle, queryString) {
  // Completion, not abandonment — suppress the exit report for the navigation
  // scheduled at the end of this function.
  jfHandingOffToPlp = true;
  // One param per question, whatever the question asked (occasion, type,
  // category) — the sheet names them by position, not by meaning.
  const tiles = {
    feature: JF_FEATURE,
    Selected_Tile_Q1: (jfAnswers[1] || []).join(", "),
    Selected_Tile_Q2: (jfAnswers[2] || []).join(", "),
    Selected_Tile_Q3: (jfAnswers[3] || []).join(", ")
  };

  if (ctReady()) {
    clevertap.event.push("finder_preferences", tiles);
  }

  // Picked up by assets/custom.js on the PLP, which adds screen_url and fires
  // visited_plp_from_gift_finder. Stored even when clevertap is not ready here —
  // the PLP checks readiness itself.
  try {
    sessionStorage.setItem(FINDER_VISITED_PLP_KEY, JSON.stringify(tiles));
  } catch (e) {}

  // The history entry pushed for the last question does not survive this
  // hand-off — a back tap on the PLP lands on the previous question's entry
  // instead (Q2 rather than Q3). Keep the view here, outside the stack, so the
  // return trip can rebuild it whatever the browser did. buildState(true) keeps
  // the breadcrumbs whole, exactly as the pushed entry would have.
  try {
    sessionStorage.setItem(JF_PLP_STATE_KEY, JSON.stringify({
      savedAt: Date.now(),
      state: buildState(true)
    }));
  } catch (e) {}

  setTimeout(() => {
    window.location.href = `/collections/${encodeURIComponent(collectionHandle)}?${queryString}`;
  }, 2500);
}

// ===============================
// Global State
// ===============================
let currentApi = "product-finder";
let currentIndex = 1;
let jewelleryFinderIndex = 0;
let currentFilter = '';
let collectionDataString;
let filtersToPut = {};
let appliedFilter = {};
let apiCalled = {};
let isJewelleryFilter = {};
let currentTileName = [];

// ===============================
// History state saving / restoring
// ===============================
// The serialisable snapshot of the current question. Split out of saveState()
// so jfGoToPlp() can stash the same shape in sessionStorage.
//
// collectionDataString is deliberately NOT carried: it is the full wizzy filter
// response (~30KB), it is re-fetched and re-read within the same click handler
// that assigns it, and nothing reads it after a restore — so persisting it only
// made every history entry heavier.
function buildState(isPush = false) {
  return {
    isJewelleryFinderState: true,
    currentIndex,
    jewelleryFinderIndex,
    currentApi,
    currentFilter,
    filtersToPut: JSON.parse(JSON.stringify(filtersToPut || {})),
    appliedFilter: JSON.parse(JSON.stringify(appliedFilter || {})),
    apiCalled: JSON.parse(JSON.stringify(apiCalled || {})),
    isJewelleryFilter: JSON.parse(JSON.stringify(isJewelleryFilter || {})),
    currentTileName: [...(currentTileName || [])],
    // Carried so the preference / back-button events still name the right tiles
    // after a back-navigation rebuilds the step from history.
    jfAnswers: JSON.parse(JSON.stringify(jfAnswers || {})),
    
    // DOM contents
    contentBoxMainHtml: jQuery(".find-content-box-main").html() || "",
    contentHeaderHtml: jQuery(".find-content-header").prop('outerHTML') || "",
    breadcrumbsHtml: (() => {
      const clone = jQuery(".kf-breadcrumbs").clone();
      clone.find(".kf-crumb").css("visibility", "");
      if (!isPush) {
        const maxCrumbs = currentIndex - 2;
        clone.find(".kf-crumb").each(function (idx) {
          if (idx >= maxCrumbs) {
            jQuery(this).remove();
          }
        });
      }
      return clone.html() || "";
    })(),
    progressUlHtml: jQuery(".rd_progress_main_ul").html() || "",
    
    // Element states
    nextBtnClassList: Array.from(document.querySelector('.Next-btn')?.classList || []),
    prevBtnStyleVisibility: document.querySelector('.Previous-btn')?.style.visibility || 'hidden',
    step1Display: 'none',
    step2Display: 'block',
    step3Display: 'none',
    
    // Checked inputs indices
    checkedIndices: Array.from(document.querySelectorAll(".find-content-box-main input")).map((inp, idx) => inp.checked ? idx : null).filter(idx => idx !== null)
  };
}

function saveState(isPush = false) {
  const state = buildState(isPush);

  try {
    if (isPush) {
      history.pushState(state, '');
    } else {
      history.replaceState(state, '');
      if (!window.initialFinderState) {
        window.initialFinderState = state;
      }
    }
  } catch (e) {
    console.error('[jewellery-find] Error writing history state:', e);
  }
}

function restoreState(state) {
  if (!state) return;
  currentIndex = state.currentIndex;
  jewelleryFinderIndex = state.jewelleryFinderIndex;
  currentApi = state.currentApi;
  currentFilter = state.currentFilter;
  filtersToPut = state.filtersToPut || {};
  appliedFilter = state.appliedFilter || {};
  apiCalled = state.apiCalled || {};
  isJewelleryFilter = state.isJewelleryFilter || {};
  currentTileName = state.currentTileName || [];
  jfAnswers = state.jfAnswers || {};

  // Restore HTML
  if (state.contentBoxMainHtml !== undefined) {
    jQuery(".find-content-box-main").html(state.contentBoxMainHtml);
  }
  if (state.contentHeaderHtml) {
    jQuery(".find-content-header").replaceWith(state.contentHeaderHtml);
  }
  if (state.breadcrumbsHtml !== undefined) {
    jQuery(".kf-breadcrumbs").html(state.breadcrumbsHtml);
    jQuery(".kf-breadcrumbs .kf-crumb").css("visibility", "");
  }
  if (state.progressUlHtml !== undefined) {
    jQuery(".rd_progress_main_ul").html(state.progressUlHtml);
  }

  // Restore button states
  const nextBtn = document.querySelector('.Next-btn');
  if (nextBtn && state.nextBtnClassList) {
    nextBtn.className = 'btn Next-btn';
    state.nextBtnClassList.forEach(cls => {
      if (cls && cls !== 'btn' && cls !== 'Next-btn') {
        nextBtn.classList.add(cls);
      }
    });
  }
  const prevBtn = document.querySelector('.Previous-btn');
  if (prevBtn) {
    // saveState() runs AFTER updateCards() bumps currentIndex, so a restored
    // state carries the one-ahead value: Q1 is currentIndex 2, not 1. Testing
    // `currentIndex === 1` therefore never matched and Back stayed on screen
    // after stepping back from Q2 to Q1. jfCurrentQuestion() applies the same
    // offset the rest of the file uses.
    prevBtn.style.visibility = (jfCurrentQuestion() === 1) ? 'hidden' : 'visible';
  }

  // Restore step sections display
  jQuery('.find_step1').css('display', state.step1Display || 'none');
  jQuery('.find_step2').css('display', state.step2Display || 'block');
  jQuery('.find_step3').css('display', state.step3Display || 'none');

  // Restore container class list (ensure main view is displayed and not hidden by inview)
  const mainView = document.querySelector('.mainFinderView');
  if (mainView) {
    mainView.classList.add('mainFinderView', 'kf-enter', 'kf-settled');
    mainView.classList.remove('kf-await-inview');
  }

  // Restore checked input states
  if (state.checkedIndices) {
    const inputs = document.querySelectorAll(".find-content-box-main input");
    inputs.forEach((inp, idx) => {
      inp.checked = state.checkedIndices.includes(idx);
      jQuery(inp).closest('.find_check_option').toggleClass('selected', inp.checked);
    });
  }

  // Remove deck/summary elements and close loaders
  jQuery('.kf-deck').remove();
  jQuery('.kf-summary').remove();
  if (typeof closeLoader === 'function') {
    closeLoader();
  }
}


// ===============================
// Returning from the PLP
//
// jfGoToPlp() stores the question it left from because the entry it was pushed
// under is gone by the time the customer taps back on the PLP — the browser
// hands us the entry BEFORE it (Q2 for a three-question run). When we land
// short like that, push the stored view so the customer sees the question they
// actually left, and a further back still walks Q2 -> Q1 behind it.
// ===============================
function jfReadPlpReturnState() {
  let raw;
  try {
    raw = sessionStorage.getItem(JF_PLP_STATE_KEY);
  } catch (e) {
    return null;
  }
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    parsed = null;
  }
  const state = parsed && parsed.state;
  if (!state || !state.isJewelleryFinderState) return null;
  if (!parsed.savedAt || (Date.now() - parsed.savedAt) > JF_PLP_STATE_MAX_AGE) return null;
  return state;
}

function jfClearPlpReturnState() {
  try {
    sessionStorage.removeItem(JF_PLP_STATE_KEY);
  } catch (e) {}
}

function jfRestoreAfterPlp() {
  const stored = jfReadPlpReturnState();
  if (!stored) return false;

  // Only when this is a step back INTO the flow. A fresh landing on the page
  // carries no finder state and must start at Q1 as usual — and must not eat
  // the stored view, which a later back tap may still need.
  const landed = history.state;
  if (!landed || !landed.isJewelleryFinderState) return false;

  jfClearPlpReturnState();

  // The browser kept the right entry — restoreState() on it does the job.
  if ((landed.currentIndex || 0) >= (stored.currentIndex || 0)) return false;

  try {
    history.pushState(stored, '');
  } catch (e) {
    console.error('[jewellery-find] Error writing history state:', e);
  }
  restoreState(stored);
  return true;
}

// Every way back into a finder page: first paint, bfcache restore, popstate.
function jfEnterFromHistory() {
  const hasFinderState = !!(history.state && history.state.isJewelleryFinderState);
  if (!hasFinderState && !window.initialFinderState) return false;

  // Back in the questionnaire, so a later abandonment is reportable again.
  jfHandingOffToPlp = false;
  jfExitReported = false;

  window.isRestoringState = true;
  if (!jfRestoreAfterPlp()) {
    if (hasFinderState) {
      restoreState(history.state);
    } else {
      restoreState(window.initialFinderState);
    }
  }
  setTimeout(() => {
    window.isRestoringState = false;
  }, 0);
  return true;
}

// ===============================
// Auto-start if referrer is homepage
// ===============================
function autoStartIfFromHome() {
  // Let user click "Show Me My Match" / "Find My Perfect Piece" explicitly
  return;
}

// ===============================
// Event: Start button (delegated)
// ===============================
jQuery(document).on("click", ".start_find_btn_submit", () => {
  // A new run invalidates any view stored for a previous PLP hand-off.
  jfClearPlpReturnState();
  jfHandingOffToPlp = false;
  jfExitReported = false;

  if (!jfStarted) {
    jfStarted = true;
    jfAnswers = {};
    if (ctReady()) {
      clevertap.event.push("finder_started", {
        feature: JF_FEATURE,
        page_name: jfEntryPage()
      });
    }
  }
  if (typeof showLoader === 'function') showLoader();

  // Ensure currentApi is defined before use
  if (typeof firstApiCall === 'function') {
    firstApiCall(currentApi);
  }
});

// ===============================
// Claiming the forward history entry
//
// The entry for the NEXT question is taken here, on the click, instead of after
// the new cards render.
//
// iOS snapshots a history entry at the moment we navigate away from it, and
// shows that snapshot during a back gesture until the page is live again. The
// push used to land ~1s into the transition, while the deck overlay covered the
// screen — so every entry's snapshot WAS the tile-selection animation, and a
// swipe back sat on that frozen animation for as long as the page took to come
// back instead of showing the question.
//
// This runs on the capture phase so it beats the deck overlay, which
// sections/jewellery-find.liquid appends from a bubble-phase handler: nothing
// has repainted yet, so the snapshot is the question the customer is leaving.
// The claimed entry carries that question until the renderer fills in the real
// one.
// ===============================
let jfForwardClaimPending = false;

document.addEventListener('click', function (event) {
  const btn = event.target && event.target.closest && event.target.closest('.Next-btn');
  // The final step hands off to the PLP rather than to another question, so it
  // has no entry to claim.
  if (!btn || btn.classList.contains('final-call')) return;
  if (window.isRestoringState || jfForwardClaimPending) return;

  // Record the question being left — with its selection — into its own entry…
  saveState(false);
  // …then take the next one while that question is still what is on screen.
  try {
    history.pushState(buildState(true), '');
    jfForwardClaimPending = true;
  } catch (e) {
    console.error('[jewellery-find] Error writing history state:', e);
  }
}, true);

// ===============================
// Event: Next button (delegated)
// ===============================
jQuery(document).on("click", ".Next-btn", async function () {
  // Forward steps already recorded this question and claimed the next entry on
  // the capture phase above. The final step claims nothing, so it records
  // itself here before the PLP hand-off.
  if (this.classList.contains('final-call')) {
    saveState(false);
  }

  if (typeof showLoader === 'function') showLoader();

  const previousBtn = getPrevBtn();
  if (previousBtn) previousBtn.style.visibility = 'visible';

  const selectedRadio = jQuery(".find_check_option [name='find_radio_btn']:checked");
  const selectedSearchString = selectedRadio.data('searchstring');
  const selectedScreenName = selectedRadio.data('screenname');
  const selectedCollectionId = selectedRadio.data('collectionid');
  const selectedfilterKey = selectedRadio.data('filterkey');
  currentTileName.push(selectedRadio.data('tilename'));

  const selectedCheckboxes = jQuery(".find_check_option [name='find_checkbox_btn']:checked");

  // Clear current step's filter selections first (to handle modified selections or unchecking all on back-navigation)
  const normCurrentKey = normalizeFilterKey(currentFilter);
  if (normCurrentKey) {
    filtersToPut[normCurrentKey] = [];
    appliedFilter[currentFilter] = [];
  }

  // ✅ Collect checkbox-based filters
  if (selectedCheckboxes.length > 0) {
    selectedCheckboxes.each(function () {
      const id = jQuery(this).data('collectionid');
      const key = jQuery(this).data('filterkey');
      const normalizedKey = normalizeFilterKey(key);
      if (!normalizedKey) return;

      currentTileName.push(id);

      filtersToPut[normalizedKey] = [...new Set([...(filtersToPut[normalizedKey] || []), id])];
      appliedFilter[currentFilter] = [...new Set([...(appliedFilter[currentFilter] || []), id])];
    });

    if (currentIndex >= 2) {
      jfTrackQuestionAnswered(currentTileName);
      currentTileName.length = 0;
    }
  }

  // ✅ Handle radio-based StaticPage or JewelleryFinderHandle
  if (selectedSearchString && selectedScreenName) {
    currentApi = selectedSearchString;

    if (currentIndex >= 2) {
      jfTrackQuestionAnswered(currentTileName);
      currentTileName.length = 0;
    }

    if (selectedScreenName === 'StaticPage') {
      await firstApiCall(currentApi);
    } else if (selectedScreenName === 'JewelleryFinderHandle') {
      await firstApiCall(currentApi, false, true);
      collectionDataString = await wizzyFilterSearch(selectedSearchString);
      const collectionCards = collectionDataString?.filters?.find?.(item => item.key === currentFilter)?.data || [];
      updateShopifyCards(collectionCards, selectedSearchString);
    }

  // ✅ Handle radio-based Shopify filter (single option)
  } else if (selectedCollectionId) {
    const normalizedKey = normalizeFilterKey(selectedfilterKey);
    if (normalizedKey) {
      filtersToPut[normalizedKey] = [...new Set([...(filtersToPut[normalizedKey] || []), selectedCollectionId])];
      appliedFilter[currentFilter] = [...new Set([...(appliedFilter[currentFilter] || []), selectedCollectionId])];
    }

    if (currentIndex >= 2) {
      jfTrackQuestionAnswered(currentTileName);
      currentTileName.length = 0;
    }

    if (this.classList.contains('final-call')) {
      jfGoToPlp(currentApi, buildQueryFromFilters(filtersToPut));
    } else {
      await firstApiCall(currentApi, false, true);
      collectionDataString = await wizzyFilterSearch(currentApi);
      const collectionCards = collectionDataString?.filters?.find?.(item => item.key === currentFilter)?.data || [];
      updateShopifyCards(collectionCards, currentApi);
    }

  // ✅ No radio/collectionId — likely final filter screen
  } else {
    if (currentIndex >= 2 && currentTileName.length > 0) {
      jfTrackQuestionAnswered(currentTileName);
      currentTileName.length = 0;
    }

    if (this.classList.contains('final-call')) {
      jfGoToPlp(currentApi, buildQueryFromFilters(filtersToPut));
    } else {
      await firstApiCall(currentApi, false, true);
      collectionDataString = await wizzyFilterSearch(currentApi);
      const collectionCards = collectionDataString?.filters?.find?.(item => item.key === currentFilter)?.data || [];
      updateShopifyCards(collectionCards, currentApi);
    }
  }
});

// Checkbox visual state
jQuery(document).on("change", "[name='find_checkbox_btn']", function () {
  jQuery(this).closest('.find_check_option').toggleClass('selected', this.checked);
});

// ===============================
// Event: Previous button (delegated)
//
// The in-flow Previous steps BACK one question. Back_Click is the question being
// left, Clicks_Lands_On the one it returns to, and Previous_Selected_Tile the
// answer already stored for that earlier question — so "on Q2, tap back" reports
// Q2 / Bridal / Q1.
// ===============================
jQuery(document).on("click", ".Previous-btn", function (e) {
  e.preventDefault();

  const leaving = jfCurrentQuestion();
  const landingOn = Math.max(leaving - 1, 1);

  if (ctReady()) {
    clevertap.event.push("finder_back_button_click", {
      feature: JF_FEATURE,
      Back_Click: `Q${leaving}`,
      Previous_Selected_Tile: (jfAnswers[landingOn] || []).join(", "),
      Clicks_Lands_On: `Q${landingOn}`
    });
  }

  history.back();
});

// ===============================
// Event: top back button — exits the finder entirely (delegated)
//
// Distinct from Previous above: this one leaves the flow rather than stepping
// within it, so it reports which question the customer walked away from.
//
// NOTE: the finder page (templates/page.find-the-one.json) has no top back
// button of its own yet. The selectors below cover the theme's existing top bar
// and a [data-jf-exit] hook, so the event starts reporting the moment that
// button is added — nothing here needs to change then.
// ===============================
// Whether the questionnaire itself is on screen. The exit selectors below also
// match the PLP top bar, and pagehide fires on every page — neither may report
// an exit from outside the flow.
function jfInQuestionnaire() {
  const step2 = document.querySelector('.find_step2');
  if (!step2) return false;
  return getComputedStyle(step2).display !== 'none';
}

// One Exit per run, from whichever path notices the customer leaving first.
function jfReportExit() {
  if (jfExitReported || jfHandingOffToPlp) return false;
  if (!jfInQuestionnaire()) return false;

  jfExitReported = true;
  const payload = { feature: JF_FEATURE, Questionnaire: `Q${jfCurrentQuestion()}` };

  if (window.KushalsNativeCleverTap) {
    window.KushalsNativeCleverTap.pushEvent("finder_exit", JSON.stringify(payload));
  } else if (ctReady()) {
    clevertap.event.push("finder_exit", payload);
  }
  return true;
}

jQuery(document).on(
  "click",
  "[data-jf-exit], [data-k-plp-back], .k-plp-topbar__back",
  function (e) {
    if (!jfInQuestionnaire()) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    jfReportExit();

    setTimeout(() => {
      window.location.href = '/';
    }, 100);
  }
);

// Any link that carries the customer off the finder page. Reported on the click
// rather than on unload, because CleverTap's queue is not guaranteed to flush
// once the page is tearing down. Anything inside the finder UI is in-flow
// navigation — tiles, Previous, the PLP hand-off — and is skipped.
jQuery(document).on("click", "a[href]", function () {
  if (!jfInQuestionnaire()) return;
  if (this.closest('.find_step1, .find_step2')) return;

  const href = this.getAttribute('href') || '';
  if (!href || href.charAt(0) === '#') return;
  if (/^(javascript:|tel:|mailto:)/i.test(href)) return;

  jfReportExit();
});

// Backstop for the paths no click covers: the back gesture off the page, a
// typed URL, and closing the tab. pagehide rather than beforeunload, because
// iOS Safari skips beforeunload when a page goes into bfcache.
window.addEventListener('pagehide', function () {
  jfReportExit();
});

// ===============================
// Main API Call
// ===============================
async function firstApiCall(currentApi, isSecondCall = false, isJewelleryFinder = false, isReverse = false) {
  const url = `${window.apiUrl}/callback/static-page/${currentApi}`;

  try {
    let data;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      data = await response.json();
    } catch (apiError) {
      const fb = await loadFallback();
      data = fb?.pages?.[currentApi];
      if (!data) throw apiError;
      console.warn('[jewellery-find] live API unavailable, using bundled snapshot for', currentApi);
    }

    jQuery('.find_step1').hide();
    jQuery('.find_step2').show();

    apiCalled[currentIndex] = currentApi;

    const col = data?.[0]?.collections?.[0];
    if (!col) {
      if (typeof closeLoader === 'function') closeLoader();
      return data;
    }

    const filters = col.filters || [];
    const collectionItem = col;

    if (isJewelleryFinder) {
      updateDetails(filters, true, isReverse);
    } else if (!isSecondCall) {
      updateDetails(filters[0] || {}, false, isReverse);
    } else {
      updateCards(collectionItem.collectionItem || []);
    }
    return data;
  } catch (error) {
    console.error('Error fetching data:', error);
    return null;
  }
}

// ===============================
// Update UI: filters / steps
// ===============================
const updateDetails = (filters, isJewelleryFinderFlow, isReverse) => {
  const updateHeader = (title, description) => {
    const questionTitle = `
      <div class="find-content-header">
        <p>${title || ""}</p>
        <h2>${description || ""}</h2>
      </div>`;
    jQuery(".find-content-header").replaceWith(questionTitle);
  };

  const toggleProgressStep = (index, isActive) => {
    const li = document.querySelector(`.rd_progress_${index}`);
    if (li) li.classList.toggle('active', !!isActive);
  };

  // --- Simple (non-Jewellery Finder) step ---
  if (!isJewelleryFinderFlow) {
    const stepIndex = isReverse ? currentIndex + 1 : currentIndex;
    const stepElement = document.querySelector(`.rd_progress_${stepIndex}`);
    const stepLabel = document.querySelector(`.rd_progress_${stepIndex} p`);

    if (isReverse) {
      stepElement?.classList?.remove('active');
    } else {
      if (stepLabel && filters?.label) stepLabel.textContent = filters.label;
      stepElement?.classList?.add('active');
    }

    updateHeader(filters?.title, filters?.description);
    firstApiCall(currentApi, true, false, isReverse);
    return;
  }

  // --- Jewellery Finder flow ---
  const progressUl = document.querySelector('.rd_progress_main_ul');

  if (isReverse) {
    toggleProgressStep(currentIndex + 1, false);
    document.querySelector('.Next-btn')?.classList?.remove('final-call');
  } else {
    toggleProgressStep(currentIndex, true);
  }

  // First time: render all steps
  if (jewelleryFinderIndex === 0 && Array.isArray(filters)) {
    filters.forEach((step, index) => {
      const liClass = `rd_progress_${currentIndex + index}`;
      let liElement = document.querySelector(`.${liClass}`);
      if (!liElement) {
        liElement = document.createElement('li');
        liElement.className = liClass;
        liElement.innerHTML = `<span></span><p>${step?.label || ""}</p>`;
        progressUl?.appendChild(liElement);
      } else {
        liElement.querySelector('p').textContent = step?.label || "";
      }
    });

    // Clean extra steps beyond current flow
    const allLi = document.querySelectorAll('.rd_progress_main_ul li');
    allLi.forEach((li, idx) => {
      if (idx >= currentIndex - 1 + filters.length) li.remove();
    });
  }

  const currentStep = Array.isArray(filters) ? filters[jewelleryFinderIndex] : null;

  if (isReverse && jewelleryFinderIndex !== 0) {
    const prevStep = filters?.[jewelleryFinderIndex - 1];
    if (prevStep?.navigation?.searchString) {
      const previousBtn = getPrevBtn();
      previousBtn?.setAttribute('data-currentFilter', prevStep.navigation.searchString);
    }

    // Remove last entries from filtersToPut/appliedFilter
    const removeLastKey = (obj) => {
      const keys = Object.keys(obj);
      if (keys.length > 0) delete obj[keys[keys.length - 1]];
    };
    removeLastKey(filtersToPut);
    removeLastKey(appliedFilter);
  }

  if (currentStep?.title || currentStep?.description) {
    updateHeader(currentStep?.title, currentStep?.description);
  }

  currentFilter = currentStep?.navigation?.searchString || currentFilter;

  if (jewelleryFinderIndex !== 0) {
    const previousBtn = getPrevBtn();
    previousBtn?.setAttribute('data-currentFilter', currentFilter);
    isJewelleryFilter[currentIndex] = currentFilter;
  }

  jewelleryFinderIndex++;

  const progressBarCount = document.querySelectorAll('.rd_progress_main_ul li').length;
  if (currentIndex === progressBarCount) {
    document.querySelector('.Next-btn')?.classList?.add('final-call');
  }
};

// ===============================
// Renderers
// ===============================
const updateCards = (collectionCards) => {
  const cardsHTML = (collectionCards || []).map((item, index) => `
    <div class="find-content-box find_check_option">
      <input type="radio" name="find_radio_btn"
        data-screenName="${item?.navigation?.screenName || ""}"
        data-searchString="${item?.navigation?.searchString || ""}"
        data-tileName="${item?.text3 || ""}">
      <label>
        <div class="img">
          <img loading="eager" fetchpriority="high" src="${item?.images?.[0].url || ""}" alt="${item?.reference || ""}">
        </div>
        <h2>${item?.text3 || ""}</h2>
      </label>
    </div>
  `).join('');

  jQuery(".find-content-box-main").html(cardsHTML);
  jfScrollToStepTop();

  const selectedCard = document.querySelector(".find-content-box-main input[name='find_radio_btn']:checked")?.closest(".find-content-box");
  // NOTE: intentionally NOT calling selectedCard.scrollIntoView() here. On this
  // page the finder sits below the homepage, so a window-level smooth scroll on
  // every render yanked the whole page down — reading as the banner/text "going
  // down" a second time on top of its entrance animation. The 2-col grid is
  // already fully visible, so no auto-scroll is needed.
  void selectedCard;

  const prevBtn = document.querySelector('.Previous-btn');
  if (prevBtn) {
    prevBtn.style.visibility = (currentIndex === 1) ? 'hidden' : 'visible';
  }

  if (typeof closeLoader === 'function') closeLoader();
  currentIndex++;

  // Fill in the entry the click already claimed with the question that just
  // rendered. Restores rebuild from a stored state and must not write back.
  if (!window.isRestoringState) {
    saveState(false);
    jfForwardClaimPending = false;
  }
};

const updateShopifyCards = (collectionCards, currentApiEnd) => {
  const cardsHTML = (collectionCards || []).map((item) => {
    const imageHandle = (item?.label || "").toLowerCase().replace(/\s+/g, '-');
    return `
      <div class="find-content-box find_check_option">
        <input type="checkbox" name="find_checkbox_btn"
          data-collectionId="${item?.key || ""}" 
          data-filterkey="${currentFilter}">
        <label>
          <div class="img">
            <img loading="eager" fetchpriority="high"
              src="https://kushals-cms-static-new.s3.ap-south-1.amazonaws.com/kushalsMobile/jewelleryFinder/${(currentApiEnd || '') + '-' + imageHandle}.png"
              alt="${item?.label || ""}">
          </div>
          <h2>${item?.label || ""}</h2>
        </label>
      </div>
    `;
  }).join('');

  jQuery(".find-content-box-main").html(cardsHTML);
  jfScrollToStepTop();

  const selectedCard = document.querySelector(".find-content-box-main input[name='find_checkbox_btn']:checked")?.closest(".find-content-box");
  // NOTE: intentionally NOT calling selectedCard.scrollIntoView() here. On this
  // page the finder sits below the homepage, so a window-level smooth scroll on
  // every render yanked the whole page down — reading as the banner/text "going
  // down" a second time on top of its entrance animation. The 2-col grid is
  // already fully visible, so no auto-scroll is needed.
  void selectedCard;

  const prevBtn = document.querySelector('.Previous-btn');
  if (prevBtn) prevBtn.style.visibility = 'visible';

  if (typeof closeLoader === 'function') closeLoader();
  currentIndex++;

  // Fill in the entry the click already claimed with the question that just
  // rendered. Restores rebuild from a stored state and must not write back.
  if (!window.isRestoringState) {
    saveState(false);
    jfForwardClaimPending = false;
  }
};

// ===============================
// Wizzy filter API
// ===============================
const wizzyFilterSearch = async (collectionHandle) => {
  try {
    const response = await fetch(`${window.apiUrl}/callback/wizzy-filter`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        key: "",
        handle: collectionHandle,
        pageNo: 1,
        sizes: [],
        attributes: Object.keys(appliedFilter).length > 0 ? appliedFilter : {},
        sort: [],
      }),
    });

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  } catch (error) {
    const fb = await loadFallback();
    const snapshot = fb?.wizzy?.[collectionHandle];
    if (snapshot) {
      console.warn('[jewellery-find] wizzy API unavailable, using bundled snapshot for', collectionHandle);
      return snapshot;
    }
    console.error('Error fetching wizzy filter:', error);
    return null;
  }
};

// ===============================
// History event listeners & Init
// ===============================
window.addEventListener('popstate', function (event) {
  if (event.state && event.state.isJewelleryFinderState) {
    window.isRestoringState = true;
    restoreState(event.state);
    setTimeout(() => {
      window.isRestoringState = false;
    }, 0);
  } else if (!event.state && window.initialFinderState) {
    window.isRestoringState = true;
    restoreState(window.initialFinderState);
    setTimeout(() => {
      window.isRestoringState = false;
    }, 0);
  }
});

window.addEventListener('pageshow', function (event) {
  const s3 = document.querySelector('.find_step3');
  const isStep3Visible = s3 && getComputedStyle(s3).display !== 'none';
  if (event.persisted || isStep3Visible) {
    if (!jfEnterFromHistory()) {
      window.isRestoringState = true;
      jQuery('.find_step1').hide();
      jQuery('.find_step2').show();
      jQuery('.find_step3').hide();
      jQuery('.kf-summary').remove();
      jQuery('.kf-deck').remove();
      if (typeof closeLoader === 'function') {
        closeLoader();
      }
      setTimeout(() => {
        window.isRestoringState = false;
      }, 0);
    }
  }
});

jfEnterFromHistory();
